import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, Badge } from "@/components/ui-bits";
import { Plus, X, Loader2, Trash2, Phone, Mail, Search, ChevronRight, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function ContractorsPanel() {
  const [contractors, setContractors] = useState([]);
  const [addCon, setAddCon] = useState(false);
  const [cForm, setCForm] = useState({ name: "", phone: "", email: "", trade: "", areas: "" });
  const [busy, setBusy] = useState(false);
  const [conExpanded, setConExpanded] = useState(false);
  const [conQ, setConQ] = useState("");

  const loadContractors = () => api.get("/contractors").then(({ data }) => setContractors(data)).catch(() => {});
  useEffect(() => { loadContractors(); }, []);

  const addContractor = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contractors", cForm);
      toast.success("Contractor added");
      setAddCon(false); setCForm({ name: "", phone: "", email: "", trade: "", areas: "" });
      loadContractors();
    } catch { toast.error("Could not add contractor"); }
    finally { setBusy(false); }
  };
  const delContractor = async (id) => { await api.delete(`/contractors/${id}`); loadContractors(); };

  const conTerm = conQ.trim().toLowerCase();
  const shownContractors = conTerm.length >= 2
    ? contractors.filter((c) => `${c.name} ${c.trade} ${c.areas || ""}`.toLowerCase().includes(conTerm))
    : contractors;
  const showConList = conExpanded || conTerm.length >= 2;

  return (
    <div data-testid="contractors-panel" className="mt-6">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button data-testid="contractors-toggle" onClick={() => setConExpanded((v) => !v)} className="flex items-center gap-1.5 group">
          <ChevronRight size={18} className={`text-slate-400 transition-transform ${conExpanded ? "rotate-90" : ""}`} />
          <h3 className="font-display text-lg font-semibold text-slate-900">Contractors</h3>
          <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{contractors.length}</span>
        </button>
        <div className="relative ml-auto w-48">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="contractor-search-input" value={conQ} onChange={(e) => setConQ(e.target.value)} placeholder="Search name or trade…"
            className="w-full pl-8 pr-7 py-1.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
          {conQ && <button data-testid="contractor-search-clear" onClick={() => setConQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={14} /></button>}
        </div>
        <button data-testid="add-contractor-btn" onClick={() => setAddCon(true)} className="border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg px-3 py-1.5 text-sm font-medium flex items-center gap-1.5"><Plus size={14} /> Add Contractor</button>
      </div>
      {showConList ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-2">
          {contractors.length === 0 && <p className="text-sm text-slate-400">No contractors yet — add your trusted trades.</p>}
          {contractors.length > 0 && shownContractors.length === 0 && <p className="text-sm text-slate-400">No contractors match your search.</p>}
          {shownContractors.map((c) => (
            <Card key={c.contractor_id} className="p-4 flex items-start justify-between" data-testid={`contractor-${c.contractor_id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1"><p className="font-semibold text-slate-900 truncate">{c.name}</p>{c.trade && <Badge>{c.trade}</Badge>}</div>
                {c.phone && <p className="text-xs text-slate-500 flex items-center gap-1"><Phone size={11} /> {c.phone}</p>}
                {c.email && <p className="text-xs text-slate-500 flex items-center gap-1"><Mail size={11} /> {c.email}</p>}
                {c.areas && <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><MapPin size={11} /> {c.areas}</p>}
              </div>
              <button data-testid={`del-contractor-${c.contractor_id}`} onClick={() => delContractor(c.contractor_id)} className="text-slate-300 hover:text-red-500"><Trash2 size={15} /></button>
            </Card>
          ))}
        </div>
      ) : (
        <p className="text-xs text-slate-400">{contractors.length} contractor{contractors.length === 1 ? "" : "s"} hidden — click “Contractors” to expand, or search above.</p>
      )}

      {addCon && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setAddCon(false)}>
          <div data-testid="add-contractor-modal" className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5"><h3 className="font-display text-xl font-bold text-slate-900">Add Contractor</h3><button onClick={() => setAddCon(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button></div>
            <form onSubmit={addContractor} className="space-y-3">
              <div><label className="block text-xs font-medium text-slate-500 mb-1">Name</label>
                <input data-testid="con-name" value={cForm.name} required onChange={(e) => setCForm({ ...cForm, name: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <div><label className="block text-xs font-medium text-slate-500 mb-1">Type of work / trade</label>
                <input data-testid="con-trade" value={cForm.trade} onChange={(e) => setCForm({ ...cForm, trade: e.target.value })} placeholder="e.g. Electrician" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Phone / number</label>
                  <input data-testid="con-phone" value={cForm.phone} onChange={(e) => setCForm({ ...cForm, phone: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Email</label>
                  <input data-testid="con-email" type="email" value={cForm.email} onChange={(e) => setCForm({ ...cForm, email: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              </div>
              <div><label className="block text-xs font-medium text-slate-500 mb-1">Areas covered</label>
                <input data-testid="con-areas" value={cForm.areas} onChange={(e) => setCForm({ ...cForm, areas: e.target.value })} placeholder="e.g. Bristol, Bath, BS postcodes" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <button type="submit" data-testid="save-contractor-btn" disabled={busy} className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : null} Add Contractor</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
